import {
  bzOptional,
  getExternalPaymentIdFromPaymentMethod,
  getPaymentMethodAdditionalInfoFromPaymentMethod,
  PaymentMethod,
} from '@breezy/shared'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form } from 'antd'
import React, { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import FormBody from '../../../../elements/Forms/FormBody'
import FormHeader from '../../../../elements/Forms/FormHeader'
import { ReactHookFormItem } from '../../../../elements/Forms/ReactHookFormItem'
import { TextAreaField } from '../../../../elements/Forms/TextAreaField'
import { TextField } from '../../../../elements/Forms/TextField'
import { SetIsDirty, useExternalIsDirty } from '../../../../hooks/useExternalIsDirty'
import { OtherPaymentDto } from '../OtherPaymentWorkflow/OtherPaymentWorkflow'
import PaymentFooterButtons from '../PaymentFooterButtons/PaymentFooterButtons'

const OtherPaymentFormSchema = z.object({
  paymentMethod: z.string().describe('Method'),
  transactionId: bzOptional(z.string().describe('Transaction ID')),
  note: bzOptional(z.string()).describe('Note'),
})

export type OtherPaymentFormData = z.infer<typeof OtherPaymentFormSchema>

type OtherPaymentFormProps = {
  onCancel?: () => void
  onSubmit: (data: OtherPaymentDto) => void
  isLoading: boolean
  footerClassName?: string
  setIsDirty?: SetIsDirty
}

const OtherPaymentForm = React.memo<OtherPaymentFormProps>(
  ({ onCancel, onSubmit, isLoading, footerClassName, setIsDirty }) => {
    const {
      control,
      formState: { errors, isDirty },
      handleSubmit,
    } = useForm<OtherPaymentFormData>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: zodResolver(OtherPaymentFormSchema),
    })

    useExternalIsDirty(setIsDirty, isDirty)

    const onSubmitInner = useCallback(
      (data: OtherPaymentFormData) => {
        const externalPaymentId = getExternalPaymentIdFromPaymentMethod(PaymentMethod.OTHER, data.transactionId)
        const paymentAdditionalInfo = getPaymentMethodAdditionalInfoFromPaymentMethod(
          PaymentMethod.OTHER,
          data.paymentMethod,
        )
        onSubmit({
          note: data.note,
          externalPaymentId,
          paymentMethodAdditionalInfo: paymentAdditionalInfo,
        })
      },
      [onSubmit],
    )

    return (
      <Form className="flex min-h-0 flex-1 flex-col" layout="vertical" onSubmitCapture={handleSubmit(onSubmitInner)}>
        <FormBody>
          <FormHeader>Payment Information</FormHeader>
          <div className="flex flex-row gap-3">
            <ReactHookFormItem
              className="flex-1"
              control={control}
              name="paymentMethod"
              label="Method"
              required
              errors={errors}
              rules={{ required: 'Payment Method is required' }}
              render={({ field }) => <TextField {...field} />}
            />
            <ReactHookFormItem
              className="flex-1"
              control={control}
              name="transactionId"
              label="Transaction ID"
              errors={errors}
              render={({ field }) => <TextField {...field} />}
            />
          </div>
          <ReactHookFormItem
            control={control}
            className="flex-1"
            name="note"
            label="Payment Memo"
            errors={errors}
            render={({ field }) => (
              <TextAreaField {...field} placeholder="(Optional) Additional details about the payment" />
            )}
          />
        </FormBody>
        <PaymentFooterButtons
          onCancel={onCancel}
          isLoading={isLoading}
          formStyle="modal"
          primaryButtonText="Record Payment"
          footerClassName={footerClassName}
          dataDdActionName="BZ Payment Workflow - Pay - Other"
        />
      </Form>
    )
  },
)

export default OtherPaymentForm
